import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { invalidateSfxCache, playSfxAsset } from "../../lib/soundBus";
import { MUSIC_MOODS, SFX_TAGS } from "../../lib/soundTags";
import { Play, Star, Trash2, Upload } from "lucide-react";

// A single centralized place to browse, preview, rename, re-tag, and pick defaults for EVERY
// music track and sound effect uploaded to this game — separate from the Scene tab's per-scene
// "which mood is playing right now" controls.
function AssetRow({ asset, tagOptions, onRename, onRetag, onSetDefault, onDelete }) {
  const [name, setName] = useState(asset.original_filename || "");

  return (
    <div data-testid={`sfx-asset-row-${asset.id}`} className="flex items-center gap-1.5 bg-black/20 border border-white/10 rounded-lg px-2 py-1.5">
      <button data-testid={`sfx-asset-play-${asset.id}`} onClick={() => playSfxAsset(asset.id)} className="text-[#D4AF37] hover:text-white shrink-0">
        <Play className="w-3.5 h-3.5" />
      </button>
      <Input
        data-testid={`sfx-asset-name-${asset.id}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name.trim() && name !== asset.original_filename && onRename(asset.id, name.trim())}
        className="bg-black/30 border-white/10 h-7 text-xs flex-1 min-w-0"
      />
      <Select value={asset.tag || ""} onValueChange={(v) => onRetag(asset.id, v)}>
        <SelectTrigger data-testid={`sfx-asset-tag-select-${asset.id}`} className="bg-black/30 border-white/10 h-7 text-[10px] w-28 shrink-0"><SelectValue /></SelectTrigger>
        <SelectContent>{tagOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <button
        data-testid={`sfx-asset-default-${asset.id}`}
        title={asset.is_default ? "Default for this category" : "Set as default"}
        onClick={() => onSetDefault(asset.id)}
        className={`shrink-0 ${asset.is_default ? "text-[#D4AF37]" : "text-[#f1e0c5]/25 hover:text-[#D4AF37]"}`}
      >
        <Star className="w-3.5 h-3.5" fill={asset.is_default ? "currentColor" : "none"} />
      </button>
      <button data-testid={`sfx-asset-delete-${asset.id}`} onClick={() => onDelete(asset.id)} className="text-[#f1e0c5]/25 hover:text-[#D90429] shrink-0">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function GmSfxTab({ game }) {
  const [music, setMusic] = useState([]);
  const [sfx, setSfx] = useState([]);
  const [musicMood, setMusicMood] = useState("explore");
  const [sfxTag, setSfxTag] = useState("attack_hit");
  const musicFileRef = useRef(null);
  const sfxFileRef = useRef(null);

  const load = () => {
    api.get("/uploads", { params: { game_id: game.id, kind: "music" } }).then((r) => setMusic(r.data));
    api.get("/uploads", { params: { game_id: game.id, kind: "sfx" } }).then((r) => setSfx(r.data));
  };
  useEffect(load, [game.id]);

  const uploadMusic = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    await api.post(`/uploads?game_id=${game.id}&kind=music&tag=${musicMood}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    load();
    toast.success(`"${musicMood}" music track uploaded`);
    e.target.value = "";
  };

  const uploadSfx = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    await api.post(`/uploads?game_id=${game.id}&kind=sfx&tag=${encodeURIComponent(sfxTag)}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    invalidateSfxCache(game.id);
    load();
    toast.success(`"${sfxTag}" sound uploaded`);
    e.target.value = "";
  };

  const patchAsset = async (id, body) => {
    await api.patch(`/uploads/${id}`, body);
    invalidateSfxCache(game.id);
    load();
  };

  const deleteAsset = async (id) => {
    await api.delete(`/uploads/${id}`);
    invalidateSfxCache(game.id);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Music Library ({music.length})</p>
        <div className="flex gap-2">
          <Select value={musicMood} onValueChange={setMusicMood}>
            <SelectTrigger data-testid="sfx-tab-music-mood-select" className="bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{MUSIC_MOODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={musicFileRef} type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="sfx-tab-music-upload-input" />
          <Button data-testid="sfx-tab-upload-music-button" size="sm" variant="secondary" className="rounded-full gap-1.5 shrink-0" onClick={() => musicFileRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> Upload
          </Button>
        </div>
        <div className="space-y-1.5">
          {music.map((a) => (
            <AssetRow
              key={a.id}
              asset={a}
              tagOptions={MUSIC_MOODS}
              onRename={(id, v) => patchAsset(id, { original_filename: v })}
              onRetag={(id, v) => patchAsset(id, { tag: v })}
              onSetDefault={(id) => patchAsset(id, { is_default: true })}
              onDelete={deleteAsset}
            />
          ))}
          {music.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No music uploaded yet.</p>}
        </div>
      </div>

      <div className="border-t border-white/10 pt-4 space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Sound Effects Library ({sfx.length})</p>
        <p className="text-[10px] text-[#f1e0c5]/30">Generic fallback sounds — play automatically for anyone who hasn't set a custom sound.</p>
        <div className="flex gap-2">
          <Select value={sfxTag} onValueChange={setSfxTag}>
            <SelectTrigger data-testid="sfx-tab-sfx-tag-select" className="bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{SFX_TAGS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={sfxFileRef} type="file" accept="audio/*" className="hidden" onChange={uploadSfx} data-testid="sfx-tab-sfx-upload-input" />
          <Button data-testid="sfx-tab-upload-sfx-button" size="sm" variant="secondary" className="rounded-full gap-1.5 shrink-0" onClick={() => sfxFileRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> Upload
          </Button>
        </div>
        <div className="space-y-1.5">
          {sfx.map((a) => (
            <AssetRow
              key={a.id}
              asset={a}
              tagOptions={SFX_TAGS}
              onRename={(id, v) => patchAsset(id, { original_filename: v })}
              onRetag={(id, v) => patchAsset(id, { tag: v })}
              onSetDefault={(id) => patchAsset(id, { is_default: true })}
              onDelete={deleteAsset}
            />
          ))}
          {sfx.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No sound effects uploaded yet.</p>}
        </div>
      </div>
    </div>
  );
}
